import { useState, useEffect, useRef } from 'react';
import useDebounce from '../../../hooks/useDebounce';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { Add, Search, TrendingUp, Assignment, CheckCircle, HourglassEmpty, Download, Label } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import StatCard from '../../dashboard/components/StatCard';
import CorrespondenceTable from '../components/CorrespondenceTable';
import CorrespondenceModalForm from '../components/CorrespondenceModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import correspondenceService from '../services/correspondenceService';
import { companyService } from '../../companies';
import { correspondenceTypeService } from '../../correspondence-types';

const CorrespondenceList = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [correspondences, setCorrespondences] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedCorrespondence, setSelectedCorrespondence] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = Mi área, 1 = Etiquetado
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (user?.companyId && !selectedCompany) {
      setSelectedCompany(user.companyId.toString());
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (selectedCompany) {
      loadTypes(selectedCompany);
    } else {
      setTypes([]);
    }
    setSelectedType('');
  }, [selectedCompany]);

  useEffect(() => {
    loadCorrespondences();
  }, [page, debouncedSearch, selectedCompany, selectedStatus, selectedType, activeTab]);

  useEffect(() => {
    loadStats();
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadTypes = async (companyId) => {
    try {
      const response = await correspondenceTypeService.getAll({ companyId, limit: 100 });
      setTypes(response.data);
    } catch (error) {
      console.error('Error al cargar tipos:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await correspondenceService.getStats({ companyId: selectedCompany });
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const loadCorrespondences = async () => {
    try {
      setLoading(true);
      const response = await correspondenceService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        status: selectedStatus,
        correspondenceTypeId: selectedType || undefined,
        page,
        limit: 10,
        ...(activeTab === 1 && { tagged: 'true' }),
      });
      setCorrespondences(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar correspondencias', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCorrespondence(null);
    setOpenModal(true);
  };

  const handleEdit = (correspondence) => {
    setSelectedCorrespondence(correspondence);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta correspondencia?')) {
      try {
        await correspondenceService.delete(id);
        setCorrespondences(prev => prev.filter(c => c.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Correspondencia eliminada exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar correspondencia', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedCorrespondence) {
        await correspondenceService.update(selectedCorrespondence.id, data);
        setCorrespondences(prev => prev.map(c =>
          c.id === selectedCorrespondence.id ? { ...c, ...data } : c
        ));
        showSnackbar('Correspondencia actualizada exitosamente', 'success');
      } else {
        await correspondenceService.create(data);
        showSnackbar('Correspondencia creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadCorrespondences();
        loadStats();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar correspondencia', 'error');
      throw error;
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleToggleRead = async (id) => {
    try {
      const response = await correspondenceService.toggleRead(id);
      setCorrespondences(prev => prev.map(c =>
        c.id === id ? { ...c, isRead: response.data.isRead } : c
      ));
    } catch (error) {
      showSnackbar('Error al cambiar estado de lectura', 'error');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Polling: silently refresh every 30 seconds when no modal is open
  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      if (!openModal) {
        loadCorrespondences();
      }
    }, 30000);
    return () => clearInterval(pollingRef.current);
  }, [debouncedSearch, selectedCompany, selectedStatus, selectedType, page, openModal, activeTab]);

  return (
    <Box>
      <PageHeader
        title="Correspondencia"
        actions={
          <>
            <TextField label="Desde" type="date" size="small" value={exportStart} onChange={(e) => setExportStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
            <TextField label="Hasta" type="date" size="small" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
            <Button variant="outlined" startIcon={<Download />} onClick={() => correspondenceService.exportExcel({ startDate: exportStart, endDate: exportEnd, companyId: selectedCompany, search: debouncedSearch, status: selectedStatus })}>
              Exportar Excel
            </Button>
            {hasPermission('correspondence.create') && (
              <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
                Nueva Correspondencia
              </Button>
            )}
          </>
        }
      />

      {/* Estadísticas */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total" value={stats.total || 0} icon={<TrendingUp />} color="#288FC6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Registradas" value={stats.registered || 0} icon={<HourglassEmpty />} color="#1B6FA8" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Asignadas" value={stats.assigned || 0} icon={<Assignment />} color="#02518C" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Cerradas" value={stats.closed || 0} icon={<CheckCircle />} color="#153250" />
        </Grid>
      </Grid>

      {/* Tabs: Mi área / Etiquetado */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); setPage(1); }}>
          <Tab label="Mi área" />
          <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Label fontSize="small" /> Etiquetado</Box>} />
        </Tabs>
      </Box>

      {/* Filtros */}
      <Box sx={{ bgcolor: '#ffffff', borderRadius: '14px', p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Buscar por título, radicado o destinatario..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setPage(1);
              }}
              label="Empresa"
            >
              <MenuItem value="">Todas</MenuItem>
              {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setPage(1);
              }}
              label="Tipo"
              disabled={types.length === 0}
            >
              <MenuItem value="">Todos</MenuItem>
              {types.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(1);
              }}
              label="Estado"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="registered">Registradas</MenuItem>
              <MenuItem value="assigned">Asignadas</MenuItem>
              <MenuItem value="closed">Cerradas</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <CorrespondenceTable
          correspondences={correspondences}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleRead={handleToggleRead}
          onRefresh={loadCorrespondences}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canEdit={hasPermission('correspondence.update')}
          canDelete={hasPermission('correspondence.delete')}
        />
      )}

      <CorrespondenceModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        correspondence={selectedCorrespondence}
        companies={companies}
        types={types}
        onCompanyChange={loadTypes}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CorrespondenceList;
